import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Heading,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Switch,
  VStack,
  HStack,
  Spinner,
  Center,
  useToast,
  Text,
  Image,
  IconButton,
  Flex,
  Card,
  CardBody,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowBackIcon, DeleteIcon } from "@chakra-ui/icons";
import { supabase } from "../../../lib/supabaseClient";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const PopupForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    content: "",
    img_url: "",
    page_url: "",
    is_visible: true,
  });

  useEffect(() => {
    if (id) {
      fetchPopup();
    }
  }, [id]);

  const fetchPopup = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("popup")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      if (data) {
        setFormData({
          title: data.title || "",
          content: data.content || "",
          img_url: data.img_url || "",
          page_url: data.page_url || "",
          is_visible: data.is_visible !== undefined ? data.is_visible : true,
        });
      }
    } catch (error) {
      console.error("팝업 조회 실패:", error);
      toast({
        title: "팝업을 불러오는데 실패했습니다",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setSaving(true);
      const fileExt = file.name.split(".").pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `popup-images/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("notice")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const {
        data: { publicUrl },
      } = supabase.storage.from("notice").getPublicUrl(filePath);

      handleInputChange("img_url", publicUrl);
      toast({
        title: "이미지가 업로드되었습니다",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("이미지 업로드 실패:", error);
      toast({
        title: "이미지 업로드에 실패했습니다",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast({
        title: "제목을 입력해주세요",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setSaving(true);
      const submitData = {
        title: formData.title.trim(),
        content: formData.content.trim(),
        img_url: formData.img_url.trim(),
        page_url: formData.page_url.trim(),
        is_visible: formData.is_visible,
      };

      if (id) {
        // 수정
        const { error } = await supabase
          .from("popup")
          .update(submitData)
          .eq("id", id);

        if (error) throw error;

        toast({
          title: "팝업이 수정되었습니다",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        // 등록
        submitData.created_at = new Date().toISOString();
        const { error } = await supabase.from("popup").insert([submitData]);

        if (error) throw error;

        toast({
          title: "팝업이 등록되었습니다",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }

      navigate("/admin/popup/list");
    } catch (error) {
      console.error("팝업 저장 실패:", error);
      toast({
        title: "팝업 저장에 실패했습니다",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Center minH="60vh">
        <Spinner size="xl" color="blue.500" />
      </Center>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50" p={6}>
      <Container maxW="container.xl">
        <VStack spacing={6} align="stretch">
          {/* 헤더 */}
          <Flex justify="space-between" align="center">
            <HStack>
              <IconButton
                icon={<ArrowBackIcon />}
                variant="ghost"
                onClick={() => navigate("/admin/popup/list")}
                aria-label="뒤로가기"
              />
              <Heading size="lg" color="gray.800">
                {id ? "팝업 수정" : "새 팝업 등록"}
              </Heading>
            </HStack>
            <HStack spacing={3}>
              <Button
                variant={previewMode ? "solid" : "outline"}
                colorScheme="blue"
                onClick={() => setPreviewMode(!previewMode)}
              >
                {previewMode ? "편집 모드" : "미리보기"}
              </Button>
            </HStack>
          </Flex>

          <form onSubmit={handleSubmit}>
            <VStack spacing={6} align="stretch">
              {/* 기본 정보 */}
              <Card>
                <CardBody>
                  <VStack spacing={4} align="stretch">
                    <FormControl isRequired>
                      <FormLabel>제목</FormLabel>
                      <Input
                        value={formData.title}
                        onChange={(e) =>
                          handleInputChange("title", e.target.value)
                        }
                        placeholder="팝업 제목을 입력하세요"
                        disabled={previewMode}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>페이지 URL (선택사항)</FormLabel>
                      <Input
                        value={formData.page_url}
                        onChange={(e) =>
                          handleInputChange("page_url", e.target.value)
                        }
                        placeholder="https://example.com"
                        disabled={previewMode}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>표시 여부</FormLabel>
                      <Switch
                        isChecked={formData.is_visible}
                        onChange={(e) =>
                          handleInputChange("is_visible", e.target.checked)
                        }
                        disabled={previewMode}
                      />
                      <Text fontSize="sm" color="gray.600" mt={1}>
                        {formData.is_visible
                          ? "홈페이지에 팝업이 표시됩니다"
                          : "팝업이 숨겨집니다"}
                      </Text>
                    </FormControl>
                  </VStack>
                </CardBody>
              </Card>

              {/* 이미지 업로드 */}
              <Card>
                <CardBody>
                  <VStack spacing={4} align="stretch">
                    <FormLabel>팝업 이미지</FormLabel>
                    {formData.img_url ? (
                      <Box position="relative">
                        <Image
                          src={formData.img_url}
                          alt="팝업 이미지"
                          maxH="300px"
                          objectFit="contain"
                          borderRadius="lg"
                        />
                        <IconButton
                          icon={<DeleteIcon />}
                          position="absolute"
                          top={2}
                          right={2}
                          colorScheme="red"
                          variant="solid"
                          size="sm"
                          onClick={() => handleInputChange("img_url", "")}
                          disabled={previewMode}
                          aria-label="이미지 삭제"
                        />
                      </Box>
                    ) : (
                      <Box
                        border="2px dashed"
                        borderColor="gray.300"
                        borderRadius="lg"
                        p={8}
                        textAlign="center"
                      >
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          disabled={previewMode || saving}
                          sx={{
                            "&::file-selector-button": {
                              border: "none",
                              outline: "none",
                              cursor: "pointer",
                              padding: "8px 16px",
                              borderRadius: "6px",
                              backgroundColor: "blue.500",
                              color: "white",
                              marginRight: "16px",
                            },
                          }}
                        />
                        <Text fontSize="sm" color="gray.600" mt={2}>
                          이미지를 업로드하면 팝업에 표시됩니다
                        </Text>
                      </Box>
                    )}
                  </VStack>
                </CardBody>
              </Card>

              {/* 내용 */}
              <Card>
                <CardBody>
                  <VStack spacing={4} align="stretch">
                    <FormLabel>내용 (마크다운 지원)</FormLabel>
                    {previewMode ? (
                      <Box
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="lg"
                        p={6}
                        bg="white"
                        minH="400px"
                      >
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {formData.content || "*내용이 없습니다*"}
                        </ReactMarkdown>
                      </Box>
                    ) : (
                      <Textarea
                        value={formData.content}
                        onChange={(e) =>
                          handleInputChange("content", e.target.value)
                        }
                        placeholder="팝업 내용을 마크다운 형식으로 입력하세요..."
                        minH="400px"
                        fontFamily="mono"
                      />
                    )}
                    <Text fontSize="sm" color="gray.600">
                      마크다운 문법을 사용하여 서식을 지정할 수 있습니다
                    </Text>
                  </VStack>
                </CardBody>
              </Card>

              {/* 저장 버튼 */}
              <HStack justify="flex-end" spacing={4}>
                <Button
                  variant="outline"
                  onClick={() => navigate("/admin/popup/list")}
                  disabled={saving}
                >
                  취소
                </Button>
                <Button
                  type="submit"
                  colorScheme="blue"
                  isLoading={saving}
                  loadingText="저장 중..."
                >
                  {id ? "수정" : "등록"}
                </Button>
              </HStack>
            </VStack>
          </form>
        </VStack>
      </Container>
    </Box>
  );
};

export default PopupForm;
